"use client";

// ЕДИНЫЙ источник иконок и акцентных цветов модулей и отделов.
//
// Раньше существовало СЕМЬ конкурирующих карт (MODULE_REGISTRY в
// departments-workspace, MODULE_ICONS в module-card, DEPT_ICON + UTILITIES ×3
// копии в home-*, RD_ITEMS в ui-kit) — один модуль на /modules и в панели отдела
// рисовался РАЗНЫМИ иконками. Теперь и страница /modules, и панель отдела, и
// главная, и навигация берут иконку/цвет отсюда → один модуль везде одинаков.
//
// Иконки — из собственного линейного пака (components/ui/icons), спецификация
// docs/reference/icon-pack.md: грид 24×24, currentColor, size в ПИКСЕЛЯХ.

import type { ComponentType, CSSProperties } from "react";
import {
  IconAnalytics, IconAssortment, IconBeaker, IconBot, IconCalculator, IconCalendar,
  IconChip, IconCollection, IconDashboard, IconFlask, IconLabel, IconLaunch,
  IconMegaphone, IconPlanChart, IconPlanning, IconRoadmap, IconStar,
  IconTasks, IconTrend, IconUsers, IconVideo, IconWallet, IconWand, IconWorkflow,
  IconAnalytics as IconPulse, IconBlogger, IconCoins, IconPackage, IconWarehouse,
  IconPalette, IconTruck, IconUser,
} from "@/components/ui/icons";

/**
 * Общий тип иконки: и пак, и lucide удовлетворяют ему (все пропсы опциональны),
 * поэтому карта из пака подставляется туда, где раньше стоял IconType.
 */
export type IconType = ComponentType<{
  size?: number;
  strokeWidth?: number;
  className?: string;
  color?: string;
  style?: CSSProperties;
  title?: string;
}>;

/** Иконка модуля — по ключу модуля из бэкенда (PortalModule.key).
 *
 * ⚠️ Ключи должны совпадать с backend `modules[].key`. Историческая часть карты
 * (telegram_bots, wb_reviews, label_maker, …) описывала ключи, которых на
 * бэкенде НЕТ, — из-за этого половина модулей везде рисовалась запасным
 * значком, и в новом меню три подряд пункта выглядели одинаково
 * (Максим, 26.08.2026). Реальные ключи ниже; старые оставлены псевдонимами,
 * чтобы не сломать возможные внешние ссылки.
 */
export const MODULE_ICON: Record<string, IconType> = {
  // Реальные ключи бэкенда
  assortment: IconAssortment,
  bots: IconBot,
  creators: IconVideo,
  dev_tracker: IconBeaker,
  hr: IconUsers,
  hr_finance: IconWallet,
  hr_pii: IconUser,
  images: IconPalette,
  labels: IconLabel,
  planning: IconPlanning,
  product_launch: IconLaunch,
  purchases: IconTruck,
  real_cost: IconCalculator,
  reviews: IconStar,      // спорное: звезда = смысл отзыва
  roadmap: IconRoadmap,
  sales: IconTrend,
  tasks: IconTasks,
  users: IconUsers,
  workflows: IconWorkflow,

  // Псевдонимы прежних ключей
  telegram_bots: IconBot,
  wb_reviews: IconStar,        // спорное: звезда = смысл отзыва (было Star/MessageSquare вразнобой)
  wb_analytics: IconAnalytics,
  label_maker: IconLabel,
  creator_reports: IconVideo,
  packaging_calc: IconCalculator,
  plan_yuc: IconPlanChart,
  rd_collection: IconCollection, // спорное: слои/стопка (швейный R&D) вместо грузовика-логистики
  it_tasks: IconTasks,
  content_factory: IconWand,

  // Синтетические вкладки отделов (lib/dept-tabs) — не модули бэкенда, но в
  // навигации стоят в одном ряду с ними, поэтому иконки живут в этой же карте.
  // Без них вся синтетика рисовалась одним запасным значком (Максим, 26.08.2026).
  __dashboard: IconDashboard,
  __report: IconTrend,
  __finance: IconWallet,
  __products: IconAssortment,
  __warehouse: IconWarehouse,
  __x10: IconLaunch,
  __mppulse: IconPulse,
  __creators: IconVideo,
  __commerce: IconCoins,
  __barters: IconPackage,
  __bloggers: IconBlogger,
  __barterers: IconUsers,
  __integrations: IconWorkflow,
};

/** Акцентный цвет модуля (плашка иконки). */
export const MODULE_COLOR: Record<string, string> = {
  // Реальные ключи бэкенда (см. предупреждение к MODULE_ICON)
  assortment: "#A855F7",
  bots: "#229ED9",
  creators: "#EC4899",
  dev_tracker: "#06B6D4",
  hr: "#F43F5E",
  hr_finance: "#22C55E",
  hr_pii: "#94A3B8",
  images: "#D946EF",
  labels: "#10B981",
  planning: "#0EA5E9",
  product_launch: "#F97316",
  purchases: "#F59E0B",
  real_cost: "#EAB308",
  reviews: "#F7A600",
  roadmap: "#8B5CF6",
  sales: "#7C5CFF",
  tasks: "#7C5CFF",
  users: "#64748B",
  workflows: "#3B82F6",

  // Псевдонимы прежних ключей
  telegram_bots: "#229ED9",
  wb_reviews: "#F7A600",
  wb_analytics: "#6366F1",
  label_maker: "#10B981",
  creator_reports: "#EC4899",
  packaging_calc: "#F59E0B",
  plan_yuc: "#0284C7",
  rd_collection: "#14B8A6",
  it_tasks: "#7C5CFF",
  content_factory: "#A855F7",
};

/** Фолбэк для модуля без записи (раньше — BarChart2, крал личность WB Analytics). */
export const MODULE_FALLBACK_ICON: IconType = IconDashboard;
export const MODULE_FALLBACK_COLOR = "#6366F1";

/** Иконка отдела — по ключу отдела (Department.key). */
export const DEPT_ICON: Record<string, IconType> = {
  marketing: IconMegaphone,
  sales: IconTrend,
  rd: IconFlask,
  "r&d": IconFlask,
  finance: IconWallet,
  hr: IconUsers,
  it: IconChip,
  planning: IconCalendar,
};
export const fallbackDeptIcon: IconType = IconDashboard;
